import { useState, type FormEvent } from "react";
import type { MovieItem } from "@/types/movie";
import { siteStats } from "@/mocks/siteContent";

interface HeroProps {
  items: MovieItem[];
  onSearch: (term: string) => void;
}

export default function Hero({ items, onSearch }: HeroProps) {
  const [term, setTerm] = useState("");

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSearch(term.trim());
  };

  return (
    <section id="top" className="relative w-full overflow-hidden bg-foreground-950">
      <img
        src="https://readdy.ai/api/search-image?query=A%20grand%20cinematic%20night%20sky%20filled%20with%20glowing%20stars%20and%20soft%20nebula%20clouds%2C%20subtle%20film-reel%20and%20projector-beam%20motif%2C%20stylized%20artistic%20abstract%20background%2C%20deep%20warm%20dark%20tones%20with%20amber%20highlights%2C%20dramatic%20lighting%2C%20high%20detail%2C%20no%20text&width=1920&height=1080&seq=starcinema-hero-bg&orientation=landscape"
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/60 to-foreground-950/85"></div>

      <div className="relative mx-auto max-w-8xl px-4 pb-16 pt-28 md:px-8 md:pb-24 md:pt-36">
        <div className="grid items-center gap-12 lg:grid-cols-[1.15fr_1fr]">
          <div className="w-full animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-400/40 bg-primary-500/15 px-3 py-1 text-xs font-medium text-primary-200">
              <i className="ri-sparkling-2-line"></i>
              免费高清 · 最新影视资源聚合
            </span>

            <h1 className="mt-5 font-heading text-3xl font-bold leading-tight text-background-50 sm:text-4xl md:text-5xl">
              星辰影院，在线观看最新电影与热门电视剧
            </h1>

            <p className="mt-4 max-w-xl text-base leading-relaxed text-background-200 md:text-lg">
              聚合最新电影、热门电视剧、动漫与综艺，画质高清、更新及时。追新剧、补经典老片，
              在这里快速找到流畅播放源，省去四处搜索的麻烦。
            </p>

            <form onSubmit={handleSubmit} className="mt-8 flex w-full max-w-xl flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-500"></i>
                <input
                  type="text"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder="搜索片名、类型，如「悬疑」「长夜长歌」"
                  aria-label="搜索影视资源"
                  className="w-full rounded-md border border-background-200/40 bg-background-50/95 py-3 pl-10 pr-4 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
                />
              </div>
              <button
                type="submit"
                className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-search-line"></i>
                搜索资源
              </button>
            </form>

            <dl className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {siteStats.map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-50/10 bg-background-50/5 px-4 py-3">
                  <dt className="text-xs text-background-300">{stat.label}</dt>
                  <dd className="mt-1 font-heading text-lg font-bold text-primary-300">
                    {stat.value}
                    <span className="ml-1 text-xs font-normal text-background-300">{stat.unit}</span>
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="w-full">
            <div className="hidden items-end justify-center gap-4 lg:flex">
              <div className="w-[38%] translate-y-8 animate-fade-up">
                <HeroPoster item={items[0]} />
              </div>
              <div className="w-[46%] animate-fade-up">
                <HeroPoster item={items[1]} featured />
              </div>
              <div className="w-[38%] translate-y-12 animate-fade-up">
                <HeroPoster item={items[2]} />
              </div>
            </div>

            <div className="no-scrollbar -mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 lg:hidden">
              {items.slice(0, 5).map((item) => (
                <div key={item.id} className="w-32 shrink-0 snap-start">
                  <HeroPoster item={item} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroPoster({ item, featured = false }: { item: MovieItem; featured?: boolean }) {
  return (
    <div className="group overflow-hidden rounded-lg border border-background-50/15 bg-foreground-900">
      <div className={`relative w-full overflow-hidden ${featured ? "aspect-[2/3]" : "aspect-[2/3]"}`}>
        <img
          src={item.poster}
          alt={`${item.title} 海报`}
          title={`${item.title} 在线观看`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground-950/90 to-transparent p-3">
          <p className="truncate text-sm font-semibold text-background-50">{item.title}</p>
          <p className="mt-0.5 flex items-center gap-1 text-xs text-background-300">
            <i className="ri-star-fill text-[10px] text-primary-400"></i>
            {item.rating.toFixed(1)} · {item.genre}
          </p>
        </div>
      </div>
    </div>
  );
}